import { type Vec2, cellCentre } from '@d2/core';

import { GROUND_ITEM_RISE, GROUND_ITEM_SIZE, MONSTER_SIZE, STAIRS_SIZE } from './art-spec';
import { gridToScreen, isoDepth } from './iso';
import { type EntityPositions } from './session';

/**
 * What is under the cursor — the whole of what a left click has to decide.
 *
 * D2's mapping, and the one Ronald asked for: a body under the pointer is a
 * swing, loot under the pointer is a pickup, **the way down under the pointer is
 * a descent**, and everything else is a walk. That decision has to be made
 * *somewhere*, and it belongs here for the same reason `pointer-gesture.ts`
 * exists — it is a question about **pixels**, which is the renderer's half of
 * the boundary, and a rule inside a Phaser scene is a rule nothing can
 * unit-test.
 *
 * ## What this file is emphatically not
 *
 * It is not a filter on what may be attacked. Whether a swing lands — the reach,
 * the line of sight, the cadence, whether the body is already dead — is
 * `@d2/core`'s `admitSwing`, judged by the server on the world it holds, and the
 * client sends an *intent* and predicts none of it (`ARCHITECTURE.md`). The same
 * goes for a pickup: whether the drop still exists, whether somebody else took
 * it a tick ago, whether it fits in the grid and where it lands are all the
 * room's, and loot binds to a character server-side. So nothing here measures a
 * distance to the player, and a click on a body across the map is forwarded
 * exactly as a click on the one at arm's length is.
 *
 * ## The boxes are the sprites
 *
 * A monster's box is the body `placeholder-art.ts` draws — `MONSTER_SIZE`,
 * anchored at the feet — and **not** its whole frame. The frame is taller and
 * wider than the body to make room for a crown that most bodies do not have and
 * a ring that most bodies do not have, and hit-testing that would mean clicking
 * clear air a tile above a monster's head and swinging at it. A drop's box is
 * its lozenge, `GROUND_ITEM_SIZE`, at the point it is drawn.
 *
 * What you see is what you click, in other words, and there is deliberately no
 * padding on either: every pixel of slack a target gains is a pixel of floor the
 * player can no longer walk to, and walking is the thing being done a hundred
 * times more often.
 *
 * ## Ties are broken the way the screen breaks them
 *
 * Two sprites overlap, and the one the player sees is the one drawn last, which
 * in this projection is the one with the greater `isoDepth` (`iso.ts`). Where
 * the depths are equal — a monster standing on the loot it is guarding, a drop
 * lying on the exit — the one painted last wins: bodies over drops, drops over
 * the stairwell, which is the order `dungeon.scene.ts` paints them in. Where
 * *those* are equal too, the id decides, for the reason `nearestTargetId` breaks
 * its tie by id: a `Map` preserves insertion order, so two clients drawing the
 * same floor would otherwise click different things depending on which snapshot
 * introduced a body first.
 *
 * ## The stairs are a cell, and only one of them
 *
 * They are the one target with no id, because a floor has exactly one way down
 * and `@d2/schema`'s `DescendPayload` names nothing at all — *"there is exactly
 * one way down from a floor and the server already knows where it is"*. So a
 * `stairs` answer carries no more than its own kind, and a `null` cell means
 * this client has not been told of a way down (the renderer sandbox), which
 * leaves a click on that square an ordinary walk.
 *
 * Nothing here measures how far the stairs are either. Whether the asker is
 * close enough to take them is the room's, judged with the same reach a pickup
 * is (`descent.room.ts`'s `canTouch`), and a click on an exit across the map is
 * forwarded exactly as a click on a body across the map is — `pursuit.ts` walks
 * there and asks again.
 *
 * ## Players are not targets
 *
 * A party member is drawn like anything else and can never be one: there is no
 * friendly fire, and `isHostile` refuses a swing at one's own kind — so a click
 * that named an ally would be a click that reliably does nothing, which is worse
 * than a click that walks.
 */

/** Something a left click would act on, rather than walk to. */
export type CursorTarget =
  /** A body: the click is a swing, and `id` is the key `WorldState.monsters` uses. */
  | { readonly kind: 'monster'; readonly id: string }
  /** A drop: the click is a pickup, and `id` is the key `WorldState.ground` uses. */
  | { readonly kind: 'item'; readonly id: string }
  /** The way down: the click is a descent, and there is nothing to name — see the header. */
  | { readonly kind: 'stairs' };

/**
 * The cursor each of the four answers is worth.
 *
 * The affordance, and the reason it is a cursor rather than a button: in an
 * isometric game with a mouse the thing a player needs to know is *whether this
 * click will do something*, and the pointer is already where they are looking. A
 * button that attacks "the nearest thing" answers a question nobody asked.
 *
 * Plain CSS cursor names, because that is what a canvas takes and what a browser
 * test can read back off the element. `s-resize` for the stairs is not a pun on
 * resizing: every browser draws it as an arrow pointing **down**, which is the
 * one thing a descent has to say, and it is the only standard cursor that says
 * it.
 */
export const CURSOR = Object.freeze({
  none: 'default',
  monster: 'crosshair',
  item: 'pointer',
  stairs: 's-resize',
});

/**
 * The name a target is known by, or the empty string for the one kind that has
 * none.
 *
 * The stairs have no id — a floor has one way down and `DescendPayload` names
 * nothing — and they never need one: this is only ever reached between two
 * targets of the same kind, and there is never a second staircase.
 */
const nameOf = (target: CursorTarget): string => ('id' in target ? target.id : '');

/**
 * Whether two answers are the same answer, `null` included.
 *
 * Here rather than in the scene because it is a fact about the *union* — the
 * stairs have no id and every other kind does — and a comparison written at the
 * call site had to reach for one. The scene's use of it is the affordance's own
 * economy: a pointer move fires many times a second and every write to
 * `canvas.style.cursor` is a style recalculation, so the cursor is only assigned
 * when this says the answer changed.
 */
export const sameTarget = (a: CursorTarget | null, b: CursorTarget | null): boolean => {
  if (a === null || b === null) return a === b;

  return a.kind === b.kind && nameOf(a) === nameOf(b);
};

/** What the pointer should look like over this answer. */
export const cursorFor = (target: CursorTarget | null): string => {
  if (target === null) return CURSOR.none;
  if (target.kind === 'monster') return CURSOR.monster;

  return target.kind === 'item' ? CURSOR.item : CURSOR.stairs;
};

/** A candidate the pointer landed on, and how near the camera it was drawn. */
interface Hit {
  readonly target: CursorTarget;
  readonly depth: number;
  /** Breaks a depth tie between a body and the loot under it. Higher is drawn later. */
  readonly layer: number;
}

/**
 * Paint order, as a number. The stairwell is part of the floor and everything
 * that can lie on it or stand on it is painted over it.
 */
const STAIRS_LAYER = -1;
const ITEM_LAYER = 0;
const MONSTER_LAYER = 1;

const within = (point: Vec2, centre: Vec2, width: number, height: number): boolean =>
  Math.abs(point.x - centre.x) <= width / 2 && Math.abs(point.y - centre.y) <= height / 2;

/**
 * The nearer of two hits: greater depth first, then the layer, then the id.
 *
 * Every comparison is total and none of them can be answered by iteration order,
 * which is the property that makes the answer the same on every client.
 */
const nearer = (a: Hit, b: Hit): Hit => {
  if (a.depth !== b.depth) return a.depth > b.depth ? a : b;
  if (a.layer !== b.layer) return a.layer > b.layer ? a : b;

  return nameOf(a.target) <= nameOf(b.target) ? a : b;
};

/**
 * What a click at this **world pixel** would act on, or `null` for open floor.
 *
 * The point is in world space, not canvas space: the caller has already put it
 * through the camera, exactly as it does before `screenToCell`.
 *
 * `stairs` is the floor's way down, or `null` on a floor this client has not
 * been told of one for — the renderer sandbox, which connects to nothing.
 */
export const targetAt = (point: Vec2, positions: EntityPositions, stairs: Vec2 | null = null): CursorTarget | null => {
  let best: Hit | null = null;

  if (stairs !== null) {
    // Drawn at the middle of its cell with no rise at all, because it is a hole
    // in the floor rather than a thing lying on it — the one flat mark a body
    // standing on the cell is *supposed* to cover.
    const centre = gridToScreen(cellCentre(stairs));

    if (within(point, centre, STAIRS_SIZE.width, STAIRS_SIZE.height)) {
      best = { target: { kind: 'stairs' }, depth: isoDepth(cellCentre(stairs)), layer: STAIRS_LAYER };
    }
  }

  for (const [id, monster] of positions.monsters) {
    const feet = gridToScreen(monster.position);
    // The body stands *on* its position, so its box hangs above the anchor
    // rather than being centred on it.
    const middle = { x: feet.x, y: feet.y - MONSTER_SIZE.height / 2 };

    if (within(point, middle, MONSTER_SIZE.width, MONSTER_SIZE.height)) {
      const hit: Hit = {
        target: { kind: 'monster', id },
        depth: isoDepth(monster.position),
        layer: MONSTER_LAYER,
      };

      best = best === null ? hit : nearer(best, hit);
    }
  }

  for (const [id, item] of positions.ground) {
    const centre = gridToScreen(cellCentre(item.cell));
    const lozenge = { x: centre.x, y: centre.y - GROUND_ITEM_RISE };

    if (within(point, lozenge, GROUND_ITEM_SIZE.width, GROUND_ITEM_SIZE.height)) {
      const hit: Hit = { target: { kind: 'item', id }, depth: isoDepth(cellCentre(item.cell)), layer: ITEM_LAYER };

      best = best === null ? hit : nearer(best, hit);
    }
  }

  return best === null ? null : best.target;
};
